import React, { useState, useRef, useCallback } from 'react';
import { Move } from 'lucide-react';

const ImagePositioner = ({ imageUrl, position, onChange, aspectRatio = '4/3' }) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Parse position string "X% Y%" into {x, y}
  const parsePosition = (pos) => {
    if (!pos) return { x: 50, y: 50 };
    const parts = pos.replace(/%/g, '').split(' ');
    return {
      x: parseFloat(parts[0]) || 50,
      y: parseFloat(parts[1]) || 50
    };
  };
  
  const currentPos = parsePosition(position);

  const handleMove = useCallback((clientX, clientY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
    onChange(`${Math.round(x)}% ${Math.round(y)}%`);
  }, [onChange]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    handleMove(e.clientX, e.clientY);
    
    const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const onMouseUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
    
    const onTouchMove = (e) => {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    };
    const onTouchEnd = () => {
      setIsDragging(false);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
  };

  if (!imageUrl) return null;

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
        <Move className="w-3 h-3" />
        Arrastra para ajustar encuadre
      </label>
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className={`relative overflow-hidden rounded-lg border-2 transition-colors select-none ${
          isDragging ? 'border-[#D4A84B] cursor-grabbing' : 'border-gray-200 cursor-grab hover:border-gray-300'
        }`}
        style={{ aspectRatio }}
      >
        <img
          src={imageUrl}
          alt="Preview"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ objectPosition: `${currentPos.x}% ${currentPos.y}%` }}
          draggable={false}
        />
        {/* Crosshair indicator */}
        <div 
          className="absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10"
          style={{ left: `${currentPos.x}%`, top: `${currentPos.y}%` }}
        >
          <div className="w-full h-full rounded-full border-2 border-white shadow-md bg-white/30" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-1.5 h-1.5 rounded-full bg-white shadow" />
          </div>
        </div>
        {/* Grid overlay while dragging */}
        {isDragging && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
            <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
            <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
            <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
          </div>
        )}
      </div>
      <p className="text-[10px] text-gray-400 text-center">
        Posición: {Math.round(currentPos.x)}% H, {Math.round(currentPos.y)}% V
      </p>
    </div>
  );
};

export default ImagePositioner;
